import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  FullscreenButton?: ReactNode;
  OpenInNewTabButton?: ReactNode;
  ReturnButton?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  FullscreenButton,
  OpenInNewTabButton,
  ReturnButton,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  // Check if context=chat is in the URL params
  const params = new URLSearchParams(window.location.search);
  const context = params.get('context');
  const showLogo = context === 'chat';

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[63px] items-center">
          <div className="absolute left-0 top-1/2 ml-2 flex -translate-y-1/2 items-center gap-2">
            {showLogo && (
              <div className="inline-flex hidden items-center lg:block">
                <img
                  src="/unomed-logo-full-white.svg"
                  alt="Unomed Logo"
                  className="h-8"
                  style={{ height: '32px' }}
                />
              </div>
            )}
          </div>
          <div className="absolute top-1/2 left-[250px] hidden h-8 -translate-y-1/2 lg:block">
            {Secondary}
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <div className="flex items-center justify-center space-x-2">{children}</div>
          </div>
          <div className="absolute right-0 top-1/2 mr-2 flex -translate-y-1/2 select-none items-center gap-2">
            <div className="hidden items-center lg:flex">
              {PatientInfo}
              <div className="border-muted mx-3 h-[25px] border-r"></div>
            </div>
            {UndoRedo}
            {FullscreenButton && <div className="ml-4 flex items-center">{FullscreenButton}</div>}
            {OpenInNewTabButton && (
              <div className="ml-2 flex items-center">{OpenInNewTabButton}</div>
            )}
            {ReturnButton && <div className="ml-2 flex items-center">{ReturnButton}</div>}
            <div className="hidden flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-muted mt-2 h-full w-full"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
